"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.typeToString = exports.stringify = void 0;
const constants_1 = require("../constants");
const closure_1 = require("../interpreter/closure");
const operators_1 = require("./operators");
function makeIndent(indent) {
    if (typeof indent === 'number') {
        if (indent > 10) {
            indent = 10;
        }
        return ' '.repeat(indent);
    }
    else {
        if (indent.length > 10) {
            indent = indent.substring(0, 10);
        }
        return indent;
    }
}
function indentify(indent, s) {
    return s
        .split('\n')
        .map(v => indent + v)
        .join('\n');
}
function isArrayLike(v) {
    return (typeof v.replPrefix === 'string' &&
        typeof v.replSuffix === 'string' &&
        typeof v.replArrayContents === 'function');
}
const stringify = (value, indent = 2, splitlineThreshold = 80) => {
    // Used to check if there are any cyclic structures
    const ancestors = new Set();
    value = operators_1.forceIt(value);
    // Precompute useful strings
    const indentString = makeIndent(indent);
    const arrPrefix = '[' + indentString.substring(1);
    const objPrefix = '{' + indentString.substring(1);
    const arrSuffix = ']';
    const objSuffix = '}';
    // Util functions
    // Determines if the array/object containing these (stringified) values
    // should be multiline and indented or oneline
    const shouldMultiline = (valueStrs) => indentString !== '' &&
        (valueStrs.join(', ').length > splitlineThreshold || valueStrs.some(s => s.includes('\n')));
    // Stringify functions
    // The real one is stringifyValue
    const stringifyArray = (xs, indentLevel) => {
        ancestors.add(xs);
        const valueStrs = xs.map(x => stringifyValue(x, 0));
        ancestors.delete(xs);
        if (shouldMultiline(valueStrs)) {
            if (xs.length === 2) {
                // It's (probably) a source list
                // Don't increase indent on second element
                // so long lists don't look like crap
                return `${arrPrefix}${indentify(indentString.repeat(indentLevel + 1), valueStrs[0]).substring(indentString.length)},
${indentify(indentString.repeat(indentLevel), valueStrs[1])}${arrSuffix}`;
            }
            else {
                // A regular array,
                // indent second element onwards to match with first element
                return `${arrPrefix}${indentify(indentString.repeat(indentLevel + 1), valueStrs.join(',\n')).substring(indentString.length)}${arrSuffix}`;
            }
        }
        else {
            return `[${valueStrs.join(', ')}]`;
        }
    };
    const stringifyArrayLike = (arrayLike, indentLevel) => {
        const prefix = arrayLike.replPrefix;
        const suffix = arrayLike.replSuffix;
        const prefixIndented = prefix + indentString.substring(prefix.length);
        const suffixIndented = suffix;
        const xs = arrayLike.replArrayContents();
        ancestors.add(arrayLike);
        const valueStrs = xs.map(x => stringifyValue(x, 0));
        ancestors.delete(arrayLike);
        if (shouldMultiline(valueStrs)) {
            // indent second element onwards to match with first element
            return `${prefixIndented}${indentify(indentString.repeat(indentLevel) + ' '.repeat(prefixIndented.length), valueStrs.join(',\n')).substring(prefixIndented.length)}${suffixIndented}`;
        }
        else {
            return `${prefix}${valueStrs.join(', ')}${suffix}`;
        }
    };
    const stringifyObject = (obj, indentLevel) => {
        ancestors.add(obj);
        const valueStrs = Object.entries(obj).map(entry => {
            const keyStr = stringifyValue(entry[0], 0);
            const valStr = stringifyValue(entry[1], 0);
            if (valStr.includes('\n')) {
                return keyStr + ':\n' + indentify(indentString, valStr);
            }
            else {
                return keyStr + ': ' + valStr;
            }
        });
        ancestors.delete(obj);
        if (shouldMultiline(valueStrs)) {
            return `${objPrefix}${indentify(indentString.repeat(indentLevel + 1), valueStrs.join(',\n')).substring(indentString.length)}${objSuffix}`;
        }
        else {
            return `{${valueStrs.join(', ')}}`;
        }
    };
    const stringifyValue = (v, indentLevel = 0) => {
        if (v === null) {
            return 'null';
        }
        else if (v === undefined) {
            return 'undefined';
        }
        else if (ancestors.has(v)) {
            return '...<circular>';
        }
        else if (v instanceof closure_1.default) {
            return v.toString();
        }
        else if (typeof v === 'string') {
            return JSON.stringify(v);
        }
        else if (typeof v !== 'object') {
            return v.toString();
        }
        else if (ancestors.size > constants_1.MAX_LIST_DISPLAY_LENGTH) {
            return '...<truncated>';
        }
        else if (typeof v.toReplString === 'function') {
            return v.toReplString();
        }
        else if (Array.isArray(v)) {
            return stringifyArray(v, indentLevel);
        }
        else if (isArrayLike(v)) {
            return stringifyArrayLike(v, indentLevel);
        }
        else {
            return stringifyObject(v, indentLevel);
        }
    };
    return stringifyValue(value, 0);
};
exports.stringify = stringify;
function typeToString(type) {
    return niceTypeToString(type);
}
exports.typeToString = typeToString;
function niceTypeToString(type, nameMap = { _next: 0 }) {
    function curriedTypeToString(t) {
        return niceTypeToString(t, nameMap);
    }
    switch (type.kind) {
        case 'primitive':
            return type.name;
        case 'variable':
            if (type.constraint && type.constraint !== 'none') {
                return type.constraint;
            }
            if (!(type.name in nameMap)) {
                // type name is not in map, so add it
                nameMap[type.name] = 'T' + nameMap._next++;
            }
            return nameMap[type.name];
        case 'list':
            return `List<${curriedTypeToString(type.elementType)}>`;
        case 'array':
            return `Array<${curriedTypeToString(type.elementType)}>`;
        case 'pair':
            const headType = curriedTypeToString(type.headType);
            // convert [T1 , List<T1>] back to List<T1>
            if (type.tailType.kind === 'list' &&
                headType === curriedTypeToString(type.tailType.elementType))
                return `List<${headType}>`;
            return `[${curriedTypeToString(type.headType)}, ${curriedTypeToString(type.tailType)}]`;
        case 'function':
            let parametersString = type.parameterTypes.map(curriedTypeToString).join(', ');
            if (type.parameterTypes.length !== 1 || type.parameterTypes[0].kind === 'function') {
                parametersString = `(${parametersString})`;
            }
            return `${parametersString} -> ${curriedTypeToString(type.returnType)}`;
        default:
            return 'Unable to infer type';
    }
}
//# sourceMappingURL=stringify.js.map